import React, { FC, useEffect, useState, useMemo } from 'react';
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import {
  GetUserPrefsNotificationPreferencesQuery,
  GetUserPrefsNotificationPreferencesQueryVariables,
} from '../../../../../__generated__/loma';
import getUserPrefsNotificationPrefs from './graphql';
import {
  useAnalyticsPost,
  NarvarEventComponents,
  NarvarEventTypes,
} from '../../../hooks/Analytics';
import useLomaClient from './useLomaClient';
import ChatContent, { storedStateSchema } from './components/ChatContent';
import useChatUIState, { ChatProvider } from './useChatUIState';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';

export { useChatUIState, ChatProvider };

function tryGetSessionStorageItem(key: string) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

const ChatImpl: FC = () => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const { client } = useLomaClient();
  const logEvent = useAnalyticsPost();

  const sublimeDeliveryNotifications = useFeatureIsOn(
    'sublime-delivery-notifications',
  );

  const { update: updateChatUIState } = useChatUIState();

  const { state } = usePageContext();
  const {
    retailerMoniker,
    orderNumbers,
    trackingNumbers,
    trackingStatus,
    carrier,
  } = state;

  const orderNumber = (orderNumbers && orderNumbers[0]) || '';
  const trackingNumber = (trackingNumbers && trackingNumbers[0]) || '';

  const {
    data: getUserPrefsNotificationPreferencesData,
    loading: getUserPrefsNotificationPreferencesLoading,
  } = useQuery<
    GetUserPrefsNotificationPreferencesQuery,
    GetUserPrefsNotificationPreferencesQueryVariables
  >(getUserPrefsNotificationPrefs, {
    skip:
      !orderNumber ||
      ['JUSTSHIPPED', 'INTRANSIT'].includes(trackingStatus ?? '') === false ||
      sublimeDeliveryNotifications === false,
    variables: {
      orderNumber,
      retailerMoniker,
    },
  });

  const sublimeSubscribed =
    getUserPrefsNotificationPreferencesData?.getUserPrefsNotificationPreferences
      ?.optIns?.[0]?.preferences[0]?.status === 'active';

  // feedback ui state is kept in localstorage for now
  const localStorage = (() => {
    if (typeof window === 'undefined') return undefined;
    try {
      // reading the localStorage property can throw if we're in an iframe
      // see: https://stackoverflow.com/questions/67645164
      return window.localStorage;
    } catch (err) {
      return undefined;
    }
  })();

  // check if feedback has been submitted already before showing the cta
  // on load. see nth-pack-feedback for where this gets set on the track page.
  useEffect(() => {
    if (localStorage && trackingNumber && trackingStatus === 'DELIVERED') {
      try {
        const feedbackStorage = localStorage.getItem(
          `feedback_${trackingNumber}`,
        );
        if (feedbackStorage) setFeedbackSubmitted(true);
      } catch (err) {
        // we can treat an error as no feedback submitted
      }
    }
  }, [trackingNumber, localStorage, trackingStatus]);

  // TODO: maybe abstract this logic out since we need chatConvo.length
  // in two places now.
  const clientSessionId = [
    retailerMoniker,
    carrier,
    trackingNumber,
    orderNumber,
  ].join('_');

  const storedState = useMemo(() => {
    const str = tryGetSessionStorageItem(`chat_${clientSessionId}`);
    if (str === null) return;
    const result = storedStateSchema.safeParse(JSON.parse(str));
    if (result.success) {
      return result.data;
    }
  }, [clientSessionId]);

  const chatConvo = storedState?.convo || [];

  const openSublimePrompt =
    !sublimeSubscribed &&
    sublimeDeliveryNotifications &&
    Boolean(orderNumber) &&
    ['JUSTSHIPPED', 'INTRANSIT'].includes(trackingStatus ?? '');

  const openFeedbackPrompt =
    !feedbackSubmitted && trackingStatus === 'DELIVERED';

  const openChatOnLoad =
    (openSublimePrompt || openFeedbackPrompt) && chatConvo.length <= 1;

  useEffect(() => {
    const baseEventLogInfo = {
      eventComponent: NarvarEventComponents.NTH_KIT_CHAT,
      eventFeature: NarvarEventComponents.NTH_KIT_CHAT,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      pageComponent: NarvarEventComponents.NTH_KIT_CHAT,
      chatClientSessionId: clientSessionId,
    };

    if (openFeedbackPrompt === true) {
      logEvent({
        ...baseEventLogInfo,
        feature: 'feedback',
      });
    } else if (openSublimePrompt === true) {
      logEvent({
        ...baseEventLogInfo,
        feature: 'delivery-notifications',
      });
    } else {
      logEvent({
        ...baseEventLogInfo,
        feature: 'default',
      });
    }
  }, [logEvent, openFeedbackPrompt, openSublimePrompt, clientSessionId]);

  useEffect(() => {
    // TODO: temporarily moved the <ChatProvider/> to track page level so the
    // help pill can access state. this is initial state that gets passed to
    // that provider here in the chat component. probably want to move it back.
    updateChatUIState({
      hadChatInteraction: false,
      openOnLoad: openChatOnLoad,
      chatOpen: openChatOnLoad,
      chatExists: true,
    });
  }, [openChatOnLoad, updateChatUIState]);

  if (!client || getUserPrefsNotificationPreferencesLoading) return null;

  return (
    <ApolloProvider client={client}>
      <ChatContent
        retailerMoniker={retailerMoniker}
        orderNumber={orderNumber}
        trackingNumber={trackingNumber}
        carrierMoniker={carrier || ''}
      />
    </ApolloProvider>
  );
};

const IfChatEnabled: FC<{ children?: React.ReactNode }> = (props) => {
  const { client } = useLomaClient();
  const growthbook = useGrowthBook();

  const [status, setStatus] = useState<
    'pending' | 'enabled' | 'disabled' | 'rejected'
  >('pending');

  const attributes = growthbook?.getAttributes();
  const deviceLayout = attributes?.['track_deviceLayout'] as string;
  const retailerMoniker = attributes?.['track_retailerMoniker'] as string;

  useEffect(() => {
    if (!client || !retailerMoniker) {
      return;
    }

    const query = gql`
      query ChatEnabled($deviceLayout: String!, $retailerMoniker: String!) {
        chatEnabled(
          attributes: {
            deviceLayout: $deviceLayout
            retailerMoniker: $retailerMoniker
          }
        )
      }
    `;

    (async () => {
      const response = await client.query<{ chatEnabled: boolean }>({
        query,
        variables: {
          retailerMoniker,
          deviceLayout,
        },
      });

      if (response.data.chatEnabled) {
        setStatus('enabled');
      } else {
        setStatus('disabled');
      }
    })().catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus('rejected');
    });
  }, [client, deviceLayout, retailerMoniker]);

  if (status === 'enabled') {
    return <>{props.children}</>;
  }

  return null;
};

const IfSessionLoaded: FC<{ children?: React.ReactNode }> = (props) => {
  const [status, setStatus] = useState<'pending' | 'resolved' | 'rejected'>(
    'pending',
  );

  useEffect(() => {
    (async () => {
      const response = await fetch('/loma/api/sessions', { method: 'POST' });
      if (!response.ok) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
      setStatus('resolved');
    })().catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus('rejected');
    });
  }, []);

  if (status === 'pending') {
    return null;
  }

  if (status === 'rejected') {
    // TODO: render an error state
    return null;
  }

  return <>{props.children}</>;
};

const Chat: FC = () => (
  <IfChatEnabled>
    <IfSessionLoaded>
      <ChatImpl />
    </IfSessionLoaded>
  </IfChatEnabled>
);

export default Chat;
